/**
 * The widget's host API for a side-car pane: the Office add-in's task pane (contract §5 of
 * docs/CONTRACT-copilot-parity-implementation.md). A pane host gets:
 *  - `layout: 'pane'`: the panel is always open and fills its containing block (gyro.css: absolutely positioned, so the
 *    host's container with `position: relative`, or the page). There is no launcher and no close, minimise, maximise or
 *    mode-toggle button, Escape never closes it, and Tab is never trapped in it; new chat and the account controls stay.
 *  - `clientTools`: tools the host runs itself during a turn. Each of the widget's own streamed asks declares them
 *    (`paneCapabilities`, plus the host's `askExtras`), and that turn's `pane_tool_call` stream events run them
 *    (paneToolCalls.ts). They are passed per call (StreamHandlers.clientTools), never kept on the GyroClient: a host
 *    that shares its client with other callers (the add-in's =GYRO.ASK cells) never offers them its tools. The buffered
 *    fallback ask (POST /v1/ask, used only when the stream route is missing) declares nothing: it has no channel for
 *    `pane_tool_call`, and gyro-svc takes the Office host from the install's proof.
 *  - `clientActions`: confirmed actions the host executes and acknowledges itself (clientActions.ts).
 *  - wider confirm-card limits for long drafts: 40,000 characters per string (a whole Word insert, contract §4.1) and
 *    60,000 in all.
 *  - document outputs: "make this a Word document / a deck / a sheet" returns a file made in the browser, as on the
 *    desktop workspace (documentOutputsFor).
 * This module holds the public types and the pure decisions. It imports nothing, so its declarations ship on their own
 * in the vendored package (scripts/build-widget-bundle.mjs).
 */
/** The web component's `layout` attribute: only "pane" opts in; anything else is the floating widget. */
export function parseLayout(value) {
    return value === 'pane' ? 'pane' : 'floating';
}
export function layoutChrome(layout) {
    const floating = layout !== 'pane';
    return { alwaysOpen: !floating, launcher: floating, closeButton: floating, windowControls: floating, modeToggle: floating, escapeCloses: floating, trapsFocus: floating };
}
/** The workspace look is every host's look (0.15.0); only 'compact' keeps the classic widget. */
export function workspaceLookFor(desktopLayout) {
    return desktopLayout !== 'compact';
}
/** The floating web panel: the workspace look on a host page. Not the desktop app (its window is the panel), not a pane. */
export function floatingWorkspaceFor(o) {
    return o.workspace && !o.desktopWorkspace && o.layout !== 'pane';
}
/**
 * When the floating panel covers the whole screen instead of floating beside the page: a phone upright (640 px wide or less)
 * or on its side (a touch screen 520 px tall or less). gyro.css uses exactly this condition. A short window on a computer (a
 * docked devtools or downloads bar, a browser zoomed to 175 % or more) keeps the 400 px panel, at the window's full height.
 */
export const FLOATING_FULL_SCREEN_QUERY = '(max-width: 640px), (max-height: 520px) and (pointer: coarse)';
/**
 * The floating panel's navigation, like the desktop's Vertical/Comfy: a drawer unless expanded on a page wider than 760 px.
 * A full-screen panel is the page, so it follows the page's width alone, as the desktop window does (Expand does nothing there).
 */
export function floatingSidebarLayout(o) {
    return (o.expanded || o.fullScreen === true) && !o.pageNarrow ? 'docked' : 'drawer';
}
/** The element's desktop-layout attribute: "compact" or "workspace"; anything else is the default (workspace). */
export function parseDesktopLayout(value) {
    return value === 'compact' || value === 'workspace' ? value : undefined;
}
/** The element's theme attribute: "auto", "system", "light" or "dark"; anything else is the layout's default. */
export function parseWidgetTheme(value) {
    return value === 'auto' || value === 'system' || value === 'light' || value === 'dark' ? value : undefined;
}
/** At most this many starters from the element's `suggestions` attribute, each of at most SUGGESTION_MAX_CHARS characters. */
export const SUGGESTIONS_MAX = 8;
export const SUGGESTION_MAX_CHARS = 120;
/**
 * The element's `suggestions` attribute: the welcome screen's starters in place of the widget's defaults, separated by
 * `|` (e.g. `What needs my attention today? | Draft a message to…`), each trimmed. An empty entry or one over
 * SUGGESTION_MAX_CHARS is dropped, the list stops at SUGGESTIONS_MAX, and an attribute that leaves nothing (or none at
 * all) keeps the defaults. React hosts pass `suggestions` as an array instead.
 */
export function parseSuggestions(value) {
    if (typeof value !== 'string')
        return undefined;
    const list = value.split('|').map((entry) => entry.trim()).filter((entry) => entry.length > 0 && entry.length <= SUGGESTION_MAX_CHARS).slice(0, SUGGESTIONS_MAX);
    return list.length ? list : undefined;
}
/**
 * A theme named in an attribute: a value with a word that starts with 'dark' or 'light' ('dark', 'theme-light', 'dark-blue',
 * 'darkmode'), not both. 'calm', 'highlight' or 'twilight' name neither.
 */
export function themeWord(value) {
    if (typeof value !== 'string')
        return null;
    const words = value.toLowerCase().split(/[^a-z]+/).filter(Boolean);
    const dark = words.some((word) => word.startsWith('dark'));
    const light = words.some((word) => word.startsWith('light'));
    return dark === light ? null : dark ? 'dark' : 'light';
}
/** A CSS `color-scheme` (or a color-scheme meta tag) that allows exactly one scheme: 'dark', 'only dark', 'light'. */
export function singleColorScheme(value) {
    if (typeof value !== 'string')
        return null;
    const words = value.toLowerCase().split(/\s+/).filter(Boolean);
    const dark = words.includes('dark');
    const light = words.includes('light');
    return dark === light ? null : dark ? 'dark' : 'light';
}
/** A CSS alpha: a number or a percentage; 1 when there is none. */
function cssAlpha(raw) {
    return raw === undefined ? 1 : raw.endsWith('%') ? Number.parseFloat(raw) / 100 : Number(raw);
}
/**
 * The relative luminance (WCAG) of an opaque enough CSS colour as a browser computes it: `rgb()`, `rgba()`, hex,
 * `color(srgb ...)`, or `oklch()` / `oklab()` (Tailwind 4's palette), whose lightness L gives about L cubed. Null for
 * anything else or a see-through colour (alpha under 0.5).
 */
export function opaqueColorLuminance(value) {
    if (typeof value !== 'string')
        return null;
    const text = value.trim().toLowerCase();
    let channels = null;
    let alpha = 1;
    const oklab = /^ok(?:lch|lab)\(\s*([\d.]+)(%?)\s[^/)]*(?:\/\s*([\d.]+%?))?\s*\)$/.exec(text);
    if (oklab) {
        const lightness = Number(oklab[1]) / (oklab[2] === '%' ? 100 : 1);
        alpha = cssAlpha(oklab[3]);
        if (!Number.isFinite(lightness) || !Number.isFinite(alpha) || alpha < 0.5)
            return null;
        return Math.min(1, Math.max(0, lightness)) ** 3;
    }
    const functional = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/.exec(text);
    const srgb = /^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?))?\s*\)$/.exec(text);
    if (functional) {
        channels = [functional[1], functional[2], functional[3]].map(Number);
        alpha = cssAlpha(functional[4]);
    }
    else if (srgb) {
        channels = [srgb[1], srgb[2], srgb[3]].map((channel) => Number(channel) * 255);
        alpha = cssAlpha(srgb[4]);
    }
    else {
        const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(text)?.[1];
        if (hex) {
            const full = hex.length <= 4 ? hex.split('').map((digit) => digit + digit).join('') : hex;
            channels = [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16));
            if (full.length === 8)
                alpha = Number.parseInt(full.slice(6, 8), 16) / 255;
        }
    }
    if (!channels || channels.some((channel) => !Number.isFinite(channel)) || !Number.isFinite(alpha) || alpha < 0.5)
        return null;
    const linear = channels.map((channel) => {
        const c = Math.min(255, Math.max(0, channel)) / 255;
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}
/** The theme attributes a host page may put on <html> or <body> (the Exante360 apps all use data-theme). */
const HOST_THEME_ATTRIBUTES = ['data-theme', 'data-bs-theme', 'data-color-scheme', 'data-mode'];
/** What the floating panel watches to follow a host's own theme switch live. */
export const HOST_THEME_WATCHED_ATTRIBUTES = [...HOST_THEME_ATTRIBUTES, 'class', 'style'];
/**
 * The host page's own light or dark, for `theme` 'auto' (the floating panel): the first that says so of
 *  1. a theme attribute on <html> or <body> (`data-theme="dark"`: Uplink, Meridian, All-Hands, Nebula, the admin console),
 *  2. a `dark` or `light` class on either (`class="dark"`),
 *  3. a page that allows one colour scheme (`color-scheme: dark`, `<meta name="color-scheme" content="dark">`),
 *  4. the page's own background (<body>, then <html>), when it paints one.
 * Null when the page says nothing: the panel then follows prefers-color-scheme. Never throws.
 */
export function hostPageTheme(doc) {
    try {
        const root = doc?.documentElement;
        if (!doc || !root)
            return null;
        const elements = [root, doc.body].filter((element) => Boolean(element));
        for (const element of elements) {
            for (const name of HOST_THEME_ATTRIBUTES) {
                const theme = themeWord(element.getAttribute(name));
                if (theme)
                    return theme;
            }
        }
        for (const element of elements) {
            const named = Array.from(element.classList).map((name) => /^(?:theme-)?(dark|light)(?:-mode|-theme)?$/i.exec(name)?.[1]?.toLowerCase()).find(Boolean);
            if (named === 'dark' || named === 'light')
                return named;
        }
        const view = doc.defaultView;
        const declared = singleColorScheme(view?.getComputedStyle(root).getPropertyValue('color-scheme'))
            ?? singleColorScheme(doc.querySelector('meta[name="color-scheme"]')?.getAttribute('content'));
        if (declared)
            return declared;
        for (const element of [doc.body, root]) {
            if (!element || !view)
                continue;
            const luminance = opaqueColorLuminance(view.getComputedStyle(element).backgroundColor);
            if (luminance !== null)
                return luminance < 0.2 ? 'dark' : 'light';
        }
    }
    catch { /* a page we cannot read says nothing */ }
    return null;
}
/** Whether `inner` (a host control with focus) lies wholly under `outer` (the open panel), so the person cannot see it. */
export function coveredBy(inner, outer) {
    return inner.width > 0 && inner.height > 0
        && inner.left >= outer.left && inner.right <= outer.right && inner.top >= outer.top && inner.bottom <= outer.bottom;
}
/** The stacking order a host sets for Gyro's launcher and floating panel (gyro.css: `z-index: var(--gyro-z-index, 1180)`). */
export const GYRO_Z_INDEX_PROPERTY = '--gyro-z-index';
/**
 * Where a host puts the launcher and the floating panel (0.15.1, gyro.css): each a length from the window's right or bottom
 * edge, 24px by default (the launcher's 16px on a phone). The panel's follow the launcher's unless the host sets them. The
 * expanded panel sits on its offsets; the 400 px panel stands 72 px above its bottom offset, over the launcher.
 */
export const GYRO_OFFSET_PROPERTIES = ['--gyro-launcher-right', '--gyro-launcher-bottom', '--gyro-panel-right', '--gyro-panel-bottom'];
/** Every custom property a host sets for Gyro's launcher and floating panel; the web component passes each on to its mount. */
export const GYRO_HOST_PROPERTIES = [GYRO_Z_INDEX_PROPERTY, ...GYRO_OFFSET_PROPERTIES];
/**
 * A custom property the <gyro-assistant> element has and its mount would not inherit (the mount is a child of <body>, not of
 * the element): one set on the element (its style attribute or a rule for it) or on an ancestor between it and <body>. Empty
 * when the element has <body>'s own value (set on :root or <body>, or nowhere): the mount inherits that by itself, live.
 * Never throws.
 */
export function forwardedCustomProperty(element, mountParent, name) {
    try {
        const view = element.ownerDocument?.defaultView;
        if (!view)
            return '';
        const own = view.getComputedStyle(element).getPropertyValue(name).trim() || (element.style?.getPropertyValue(name) ?? '').trim();
        if (!own)
            return '';
        const inherited = mountParent ? view.getComputedStyle(mountParent).getPropertyValue(name).trim() : '';
        return own === inherited ? '' : own;
    }
    catch {
        return '';
    }
}
/** The element's page-shortcuts attribute: only "false" turns Gyro's page-wide shortcuts off. */
export function parsePageShortcuts(value) {
    return value !== 'false';
}
/**
 * Whether an answer can become a file the widget makes in the browser (documentGenerator: PDF, Word, Excel,
 * PowerPoint, Markdown, CSV and the rest): "make this a Word document / a deck / a sheet" is resolved, the model is told
 * the client makes the file, and the reply offers it for download. The desktop workspace does; so does a side-car pane
 * (contract §5 and §12.11: the strategy's out-of-host file generation). The floating web widget keeps answers in chat.
 */
export function documentOutputsFor(options) {
    return options.desktopWorkspace || options.layout === 'pane';
}
export const DESKTOP_EXECUTING_LABEL = 'Running on your PC';
/** A host with client actions that named no label of its own. */
export const CLIENT_EXECUTING_LABEL = 'Running in this app';
/** The running-card wording: the host's label, the generic one when it gave none, the desktop's without client actions. */
export function executingLabelFor(actions) {
    if (!actions)
        return DESKTOP_EXECUTING_LABEL;
    const label = typeof actions.executingLabel === 'string' ? actions.executingLabel.replace(/\s+/g, ' ').trim() : '';
    return label ? label.slice(0, 80) : CLIENT_EXECUTING_LABEL;
}
// ---- What a pane adds to every ask ----
export const PANE_CAPABILITIES_MAX = 16;
export const PANE_CAPABILITY_MAX_CHARS = 64;
/** A host that has not said what it can do within this long is sent as able to do nothing, so the ask never stalls. */
export const PANE_CAPABILITIES_TIMEOUT_MS = 3_000;
const CAPABILITY = new RegExp(`^[A-Za-z0-9][A-Za-z0-9._:-]{0,${PANE_CAPABILITY_MAX_CHARS - 1}}$`);
/** At most 16 distinct capability names of at most 64 characters, in the host's order. Anything else is dropped. */
export function normalizePaneCapabilities(value) {
    if (!Array.isArray(value))
        return [];
    const capabilities = [];
    for (const entry of value) {
        if (typeof entry !== 'string')
            continue;
        const capability = entry.trim();
        if (!CAPABILITY.test(capability) || capabilities.includes(capability))
            continue;
        capabilities.push(capability);
        if (capabilities.length === PANE_CAPABILITIES_MAX)
            break;
    }
    return capabilities;
}
/** Ask-body fields the widget and the desktop own; a host's extras never replace them. */
const RESERVED_ASK_FIELDS = new Set([
    'app', 'turnId', 'tz', 'question', 'history', 'conversationId', 'style', 'pageContext', 'attachments', 'model', 'effort',
    'knowledgeMode', 'paneCapabilities', 'deviceCapabilities',
]);
/** The host's extra ask fields: the own keys of a plain object, without the reserved ones. Anything else gives {}. */
export function safeAskExtras(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null)
        return {};
    const extras = {};
    for (const [key, entry] of Object.entries(value)) {
        if (RESERVED_ASK_FIELDS.has(key) || key === '__proto__' || entry === undefined || typeof entry === 'function')
            continue;
        extras[key] = entry;
    }
    return extras;
}
/**
 * What a pane host adds to an ask: its extra fields, then `paneCapabilities`. Without client tools it adds nothing.
 * It never throws and never waits longer than `timeoutMs`. A host that cannot say what it can do is sent as able to do
 * nothing (the turn then gets no pane tools), with a warning, rather than holding the person's question.
 */
export async function paneAskFields(tools, timeoutMs = PANE_CAPABILITIES_TIMEOUT_MS) {
    if (!tools)
        return {};
    let extras = {};
    try {
        extras = safeAskExtras(tools.askExtras?.());
    }
    catch (error) {
        console.warn(`[gyro] the host's ask extras failed (${errorName(error)}); asking without them`);
    }
    let timer;
    let capabilities = [];
    try {
        capabilities = normalizePaneCapabilities(await Promise.race([
            Promise.resolve().then(() => tools.capabilities()),
            new Promise((_resolve, reject) => {
                timer = setTimeout(() => reject(new Error(`no answer within ${timeoutMs} ms`)), timeoutMs);
            }),
        ]));
    }
    catch (error) {
        console.warn(`[gyro] the host's tool capabilities are unavailable (${error instanceof Error ? error.message : errorName(error)}); this ask declares none`);
    }
    finally {
        if (timer !== undefined)
            clearTimeout(timer);
    }
    return { ...extras, paneCapabilities: capabilities };
}
function errorName(error) {
    return error instanceof Error ? error.name : typeof error;
}
/** A gyro-svc API path a host may name for results and acknowledgements: `/v1/...`, no query, no `.` or `..` segment. */
export function isServiceApiPath(path) {
    return typeof path === 'string'
        && path.length <= 200
        && /^\/v1(?:\/[A-Za-z0-9._~-]+)+$/.test(path)
        && !path.split('/').some((segment) => segment === '.' || segment === '..');
}
/**
 * The compact card (512 characters a string, 4,096 in all): the original local file tasks keep it (their requests are
 * sized for it, and the PC measures a creation request against it), it is the formatting helpers' default, and a card
 * above it is wide (isWideConfirm). Every other desktop and web card is wide since 2026-10-12 (STANDARD_CONFIRM_LIMITS).
 */
export const DEFAULT_CONFIRM_LIMITS = Object.freeze({ maxStringChars: 512, maxTotalChars: 4_096 });
/**
 * Long drafts (a reply, a document insert, a slide's notes) show in full on Office cards: the longest draft a tool takes
 * (word_insert_text, 40,000 characters) fits one string, so a card never confirms text it did not show.
 */
export const OFFICE_CONFIRM_LIMITS = Object.freeze({ maxStringChars: 40_000, maxTotalChars: 60_000 });
export const OFFICE_APP = 'office';
/**
 * Gyro's built-in local providers on the desktop (desktop/src/Gyro.Mcp/LocalFiles, owner 2026-10-08: local computer
 * actions with approval): a batch of up to 200 renames, moves, copies or deletes, a written file of up to 15,000
 * characters and a PowerShell script of up to 12,000 show in full, in the tall scrollable view, so the card never
 * confirms an item it did not show. Their requests are at most 16,000 bytes (the mcp_call limit).
 */
export const LOCAL_ACTION_CONFIRM_LIMITS = Object.freeze({ maxStringChars: 16_000, maxTotalChars: 40_000 });
/**
 * Every other desktop and web card (2026-10-12): the local actions' limits. The readable card (ActionDetails.tsx) wraps
 * and scrolls a long value, so a drafted e-mail of a few thousand characters shows whole and can be confirmed; the compact
 * card refused it ("Parameters exceed the safe display limit", a 1,300-character Microsoft 365 draft on the desktop).
 * gyro-core caps every tool string at 8,000 characters, so an ordinary action's text always fits one string.
 */
export const STANDARD_CONFIRM_LIMITS = LOCAL_ACTION_CONFIRM_LIMITS;
const LOCAL_PROVIDERS = new Set(['gyro-local-files', 'gyro-local-commands']);
/**
 * The original local file tasks (PR #220) keep the compact card: their requests are sized for it, and the PC refuses a
 * creation request whose card would need more than 4,096 characters, so a taller card would let a person confirm a
 * request the PC then refuses (the 2026-10-03 review's stage-5 failure).
 */
const DEFAULT_CARD_LOCAL_TOOLS = new Set(['search_files', 'read_file', 'create_folder_structure']);
/** The mcp_call to a built-in local provider, as the card reads it: its server (case-insensitively) and tool. */
function localProviderCall(action) {
    if (!action || action.tool !== 'mcp_call' || !action.input || typeof action.input !== 'object')
        return null;
    const { server, tool } = action.input;
    return typeof server === 'string' && LOCAL_PROVIDERS.has(server.trim().toLowerCase()) ? { tool } : null;
}
/**
 * True for an mcp_call to one of the local actions of Gyro's built-in local providers (the server name is matched
 * case-insensitively): the batch changes, write, open, the listing and run_powershell, never the original three tasks.
 */
export function isLocalProviderAction(action) {
    const call = localProviderCall(action);
    return call !== null && !(typeof call.tool === 'string' && DEFAULT_CARD_LOCAL_TOOLS.has(call.tool));
}
/** True for an mcp_call to one of the original local file tasks (search_files, read_file, create_folder_structure). */
export function isOriginalLocalFileTask(action) {
    const call = localProviderCall(action);
    return call !== null && typeof call.tool === 'string' && DEFAULT_CARD_LOCAL_TOOLS.has(call.tool);
}
/**
 * The limits a confirm card uses. `override` (the widget's `confirmLimits` prop) wins: 'office' selects the Office
 * limits, and an object sets either limit, capped at the Office ones. Otherwise the Office limits apply when any of
 * `apps` (the client's app, the widget's app key, the action's app and target) is 'office', the compact card when the
 * action is one of the original local file tasks (`originalLocalTask`), and the standard desktop and web limits for
 * everything else, a local action included.
 */
export function confirmLimitsFor(options) {
    const { override } = options;
    if (override === 'office')
        return { ...OFFICE_CONFIRM_LIMITS };
    const base = options.apps?.some((app) => app === OFFICE_APP) ? OFFICE_CONFIRM_LIMITS
        : options.localAction ? LOCAL_ACTION_CONFIRM_LIMITS
            : options.originalLocalTask ? DEFAULT_CONFIRM_LIMITS : STANDARD_CONFIRM_LIMITS;
    if (!override || typeof override !== 'object')
        return { ...base };
    return {
        maxStringChars: limit(override.maxStringChars, base.maxStringChars, OFFICE_CONFIRM_LIMITS.maxStringChars),
        maxTotalChars: limit(override.maxTotalChars, base.maxTotalChars, OFFICE_CONFIRM_LIMITS.maxTotalChars),
    };
}
function limit(value, fallback, ceiling) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? Math.min(value, ceiling) : fallback;
}
/**
 * Limits above the compact card's (every card but the original local file tasks', since 2026-10-12). Such a card shows
 * its parameters in a tall scrollable view, and only the character limits can block Confirm: nesting, field and
 * list-length limits are lifted.
 */
export function isWideConfirm(limits) {
    return limits.maxStringChars > DEFAULT_CONFIRM_LIMITS.maxStringChars || limits.maxTotalChars > DEFAULT_CONFIRM_LIMITS.maxTotalChars;
}
//# sourceMappingURL=paneHost.js.map